import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  BadgeCheck,
  Building2,
  Coins,
  Crown,
  Edit3,
  Gift,
  KeyRound,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Trash2,
  UserRoundCog,
  Users,
  WalletCards,
  X,
} from "lucide-react";
import { type ReactNode, type SyntheticEvent, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  activateMeroCreditRule,
  archivePlatformUser,
  archiveMeroCreditRule,
  archiveTierRule,
  createMeroCreditRuleDraft,
  createPlatformMeroCreditRule,
  createTierRule,
  createTierRuleDraft,
  deleteMeroCreditRule,
  deleteTierRule,
  fetchMeroCreditConfiguration,
  fetchMeroCreditDashboard,
  fetchMeroCreditMerchantAcceptance,
  fetchMeroCreditRules,
  fetchPlatformSettings,
  fetchPlatformSummary,
  fetchPlatformUsers,
  fetchTierAdminProgram,
  messageFromError,
  pauseMeroCreditRule,
  publishTierRule,
  runTierAssessment,
  sendPlatformUserPasswordReset,
  setPlatformUserStatus,
  updateMeroCreditRule,
  updateMeroCreditMerchantAcceptance,
  updatePlatformSettings,
  updatePlatformTenant,
  updatePlatformUser,
  updateTierRule,
  type MeroCreditRule,
  type MeroCreditRuleInput,
  type MeroCreditMerchantAcceptanceWorkspace,
  type PlatformSettings,
  type PlatformUser,
  type TierAdminRule,
  type TierRuleInput,
  type Tenant,
} from "../api";
import { AppAlert, type AppAlertTone } from "../components/AppAlertDialog";
import { NepalAddressFields } from "../components/NepalAddressFields";
import { ArchiveAccountDialog } from "../components/ArchiveAccountDialog";
import { PlatformTierCard } from "../components/PlatformTierCard";
import "./super-admin.css";

const number = (value: number) => new Intl.NumberFormat("en-US").format(value);
const money = (minor: number) =>
  new Intl.NumberFormat("en-NP", { style: "currency", currency: "NPR" }).format(minor / 100);
const formatCredits = (minor: number) =>
  new Intl.NumberFormat("en-NP", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(minor / 100);
const date = (value: string | null) =>
  value ? new Intl.DateTimeFormat("en-NP", { dateStyle: "medium" }).format(new Date(value)) : "—";
const dateTime = (value: string) =>
  new Intl.DateTimeFormat("en-NP", { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  );
export const dateTimeLocal = (value?: string | null, timezoneOffsetMinutes?: number) => {
  const instant = value ? new Date(value) : new Date(Date.now() + 60 * 60 * 1_000);
  const offset = timezoneOffsetMinutes ?? instant.getTimezoneOffset();
  return new Date(instant.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

function Loading({ label }: { label: string }) {
  return <div className="loading-block">{label}…</div>;
}

function Modal({
  title,
  eyebrow,
  close,
  children,
}: {
  title: string;
  eyebrow: string;
  close: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <section
        className="modal sa-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sa-modal-title"
      >
        <header>
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h2 id="sa-modal-title">{title}</h2>
          </div>
          <button className="icon-button" type="button" onClick={close} aria-label="Close">
            <X />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function ActionMessage({
  message,
  clear,
  tone = "error",
}: {
  message: string;
  clear: () => void;
  tone?: AppAlertTone;
}) {
  return message ? <AppAlert message={message} onDismiss={clear} tone={tone} /> : null;
}

export function SuperAdminHomeView() {
  const summary = useQuery({ queryKey: ["platform-summary"], queryFn: fetchPlatformSummary });
  const tiers = useQuery({ queryKey: ["tier-admin-program"], queryFn: fetchTierAdminProgram });
  const credits = useQuery({
    queryKey: ["mero-credits", "admin", "dashboard"],
    queryFn: fetchMeroCreditDashboard,
  });
  if (summary.isLoading || tiers.isLoading || credits.isLoading)
    return <Loading label="Opening platform overview" />;
  if (
    summary.isError ||
    tiers.isError ||
    credits.isError ||
    !summary.data ||
    !tiers.data ||
    !credits.data
  ) {
    return <AppAlert message={messageFromError(summary.error ?? tiers.error ?? credits.error)} />;
  }
  const data = summary.data;
  return (
    <main className="portal-content sa-page">
      <section className="welcome-row sa-home-hero">
        <div>
          <p className="eyebrow">Platform command centre</p>
          <h1>Good morning. Here’s Mero Perks.</h1>
          <p>
            Monitor businesses, members, rewards, Tier Points, and platform-funded value from one
            place.
          </p>
        </div>
        <div className="sa-home-actions">
          <Link className="button button--primary" to="/super-admin/tenants">
            <Building2 /> Manage Business Owners
          </Link>
          <Link className="button button--outline" to="/super-admin/earning-rules">
            <Settings2 /> Earning rules
          </Link>
        </div>
      </section>
      <section className="sa-summary-grid" aria-label="Platform summary">
        <article>
          <span>
            <Building2 />
          </span>
          <div>
            <small>Business Owners</small>
            <strong>{number(data.tenants.total)}</strong>
            <p>
              {number(data.tenants.active)} active · {number(data.tenants.suspended)} suspended
            </p>
          </div>
        </article>
        <article>
          <span>
            <Users />
          </span>
          <div>
            <small>Platform users</small>
            <strong>{number(data.users.total)}</strong>
            <p>
              {number(data.customers.total)} customers · {number(data.tenantStaff.total)} staff
            </p>
          </div>
        </article>
        <article>
          <span>
            <Gift />
          </span>
          <div>
            <small>Rewards</small>
            <strong>{number(data.rewards.active)}</strong>
            <p>{number(data.redemptions.total)} reward redemptions</p>
          </div>
        </article>
        <article>
          <span>
            <WalletCards />
          </span>
          <div>
            <small>Mero Credits liability</small>
            <strong>{formatCredits(data.credits.outstandingMinor)}</strong>
            <p>{number(data.credits.wallets)} wallets</p>
          </div>
        </article>
      </section>
      <div className="sa-home-grid">
        <section className="portal-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Mero status</p>
              <h2>Tier program health</h2>
              <p>{number(data.tierMembers.total)} members currently tracked across the platform.</p>
            </div>
            <Crown />
          </div>
          <div className="sa-tier-gallery" aria-label="Mero Perks tier cards">
            {tiers.data.tiers.map((tier) => (
              <PlatformTierCard key={tier.publicId} tierCode={tier.code} tierName={tier.name}>
                <div className="sa-tier-card-copy">
                  <small>{number(tier.minimumTierPoints)}+ Tier Points</small>
                  <strong>{tier.name}</strong>
                  <p>{tier.description}</p>
                </div>
              </PlatformTierCard>
            ))}
          </div>
          <Link className="sa-inline-link" to="/super-admin/earning-rules">
            Review Tier Point rules →
          </Link>
        </section>
        <section className="portal-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Platform value</p>
              <h2>Mero Credits pulse</h2>
              <p>Current issuance, liability, and risk posture.</p>
            </div>
            <Coins />
          </div>
          <dl className="sa-health-list">
            <div>
              <dt>Credits issued</dt>
              <dd>{formatCredits(credits.data.ledger.issuedMinor)}</dd>
            </div>
            <div>
              <dt>Settlement payable</dt>
              <dd>{formatCredits(credits.data.settlements.payableMinor)}</dd>
            </div>
            <div>
              <dt>Active earning rules</dt>
              <dd>{number(credits.data.rules.activeRules)}</dd>
            </div>
            <div>
              <dt>Open risk events</dt>
              <dd>{number(credits.data.risks.openRiskEvents)}</dd>
            </div>
          </dl>
          <Link className="sa-inline-link" to="/super-admin/settings">
            Open platform settings →
          </Link>
        </section>
      </div>
    </main>
  );
}

function UserEditor({ user, close }: { user: PlatformUser; close: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phone: user.phone ?? "",
    profileImageUrl: user.profileImageUrl ?? "",
    birthday: user.birthday ?? "",
    addressLine1: user.addressLine1 ?? "",
    addressLine2: user.addressLine2 ?? "",
    city: user.city ?? "",
    district: user.district ?? "",
    province: user.province ?? "",
    country: user.country ?? "",
    postalCode: user.postalCode ?? "",
  });
  const [error, setError] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      updatePlatformUser(user.publicId, {
        ...form,
        phone: form.phone.trim() || null,
        profileImageUrl: form.profileImageUrl.trim() || null,
        birthday: form.birthday || null,
        addressLine1: form.addressLine1.trim() || null,
        addressLine2: form.addressLine2.trim() || null,
        city: form.city.trim() || null,
        district: form.district.trim() || null,
        province: form.province.trim() || null,
        country: form.country.trim() || null,
        postalCode: form.postalCode.trim() || null,
      }),
  });
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    try {
      await mutation.mutateAsync();
      await queryClient.invalidateQueries({ queryKey: ["platform-users"] });
      close();
    } catch (caught) {
      setError(messageFromError(caught));
    }
  };
  return (
    <Modal title="Edit user" eyebrow="Platform identity" close={close}>
      <form className="modal-form form-stack" onSubmit={(event) => void submit(event)}>
        {error && <AppAlert message={error} />}
        <fieldset className="sa-editor-section">
          <legend>Identity and contact</legend>
          <div className="form-row">
            <label>
              <span>First name</span>
              <input
                required
                value={form.firstName}
                onChange={(event) => {
                  setForm((current) => ({ ...current, firstName: event.target.value }));
                }}
              />
            </label>
            <label>
              <span>Last name</span>
              <input
                required
                value={form.lastName}
                onChange={(event) => {
                  setForm((current) => ({ ...current, lastName: event.target.value }));
                }}
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              <span>Email</span>
              <input
                required
                type="email"
                value={form.email}
                onChange={(event) => {
                  setForm((current) => ({ ...current, email: event.target.value }));
                }}
              />
              <small>Changing the email requires the new address to be verified.</small>
            </label>
            <label>
              <span>Phone</span>
              <input
                inputMode="tel"
                value={form.phone}
                onChange={(event) => {
                  setForm((current) => ({ ...current, phone: event.target.value }));
                }}
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              <span>Birthday</span>
              <input
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                value={form.birthday}
                onChange={(event) => {
                  setForm((current) => ({ ...current, birthday: event.target.value }));
                }}
              />
            </label>
            <label>
              <span>Profile photo URL</span>
              <input
                type="url"
                placeholder="https://…"
                value={form.profileImageUrl}
                onChange={(event) => {
                  setForm((current) => ({ ...current, profileImageUrl: event.target.value }));
                }}
              />
            </label>
          </div>
        </fieldset>

        <fieldset className="sa-editor-section">
          <legend>Address</legend>
          <label>
            <span>Address line 1</span>
            <input
              value={form.addressLine1}
              onChange={(event) => {
                setForm((current) => ({ ...current, addressLine1: event.target.value }));
              }}
            />
          </label>
          <label>
            <span>Address line 2</span>
            <input
              value={form.addressLine2}
              onChange={(event) => {
                setForm((current) => ({ ...current, addressLine2: event.target.value }));
              }}
            />
          </label>
          <NepalAddressFields
            value={{ province: form.province, district: form.district, localLevel: form.city }}
            onChange={({ province, district, localLevel }) => { setForm((current) => ({ ...current, province, district, city: localLevel })); }}
          />
          <div className="form-row form-row--three">
            <label>
              <span>Postal code</span>
              <input
                value={form.postalCode}
                onChange={(event) => {
                  setForm((current) => ({ ...current, postalCode: event.target.value }));
                }}
              />
            </label>
            <label>
              <span>Country</span>
              <select
                value={form.country || "Nepal"}
                onChange={(event) => {
                  setForm((current) => ({ ...current, country: event.target.value }));
                }}
              >
                <option value={form.country || "Nepal"}>Nepal</option>
              </select>
            </label>
          </div>
        </fieldset>

        <section className="sa-editor-meta" aria-labelledby="user-account-metadata">
          <h3 id="user-account-metadata">Account record</h3>
          <dl>
            <div>
              <dt>User ID</dt>
              <dd>{user.publicId}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{user.status}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{user.emailVerified ? "Verified" : "Not verified"}</dd>
            </div>
            <div>
              <dt>Roles</dt>
              <dd>{user.roles.join(", ") || "None"}</dd>
            </div>
            <div>
              <dt>Business Owners</dt>
              <dd>{user.tenantNames.join(", ") || "None"}</dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>{dateTime(user.createdAt)}</dd>
            </div>
            <div>
              <dt>Last updated</dt>
              <dd>{dateTime(user.updatedAt)}</dd>
            </div>
          </dl>
          <p>
            Roles, account status, and verification state are managed by their dedicated security
            actions.
          </p>
        </section>
        <div className="modal-actions">
          <button className="button button--quiet" type="button" onClick={close}>
            Cancel
          </button>
          <button className="button button--primary" disabled={mutation.isPending} type="submit">
            {mutation.isPending ? "Saving…" : "Save user"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function PlatformTenantEditor({ tenant, close }: { tenant: Tenant; close: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    businessName: tenant.name,
    legalName: tenant.legalName ?? "",
    slug: tenant.slug,
    businessType: tenant.businessType,
    businessEmail: tenant.contactEmail,
    businessPhone: tenant.businessPhone ?? "",
    website: tenant.website ?? "",
    defaultCurrency: tenant.defaultCurrency,
    defaultTimezone: tenant.defaultTimezone,
    country: tenant.country,
    address: tenant.address ?? "",
    city: tenant.city ?? "",
    district: tenant.district ?? "",
    stateProvince: tenant.stateProvince ?? "",
    postalCode: tenant.postalCode ?? "",
    logoUrl: tenant.logoUrl ?? "",
    primaryColor: tenant.primaryColor,
    secondaryColor: tenant.secondaryColor,
    accentColor: tenant.accentColor,
    planId: tenant.plan?.id ?? "plan-basic",
  });
  const [error, setError] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      updatePlatformTenant(tenant.publicId, {
        ...form,
        legalName: form.legalName || null,
        businessPhone: form.businessPhone || null,
        website: form.website || null,
        address: form.address || null,
        city: form.city || null,
        district: form.district || null,
        stateProvince: form.stateProvince || null,
        postalCode: form.postalCode || null,
        logoUrl: form.logoUrl || null,
      }),
  });
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    try {
      await mutation.mutateAsync();
      await queryClient.invalidateQueries({ queryKey: ["tenants"] });
      close();
    } catch (caught) {
      setError(messageFromError(caught));
    }
  };
  return (
    <Modal title={`Edit ${tenant.name}`} eyebrow="Business Owner profile" close={close}>
      <form className="modal-form form-stack" onSubmit={(event) => void submit(event)}>
        {error && <AppAlert message={error} />}
        <fieldset className="sa-editor-section">
          <legend>Business identity</legend>
          <div className="form-row">
            <label>
              <span>Business name</span>
              <input
                required
                value={form.businessName}
                onChange={(event) => {
                  setForm((current) => ({ ...current, businessName: event.target.value }));
                }}
              />
            </label>
            <label>
              <span>Legal name</span>
              <input
                value={form.legalName}
                onChange={(event) => {
                  setForm((current) => ({ ...current, legalName: event.target.value }));
                }}
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              <span>Business code</span>
              <input
                required
                value={form.slug}
                onChange={(event) => {
                  setForm((current) => ({
                    ...current,
                    slug: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
                  }));
                }}
              />
            </label>
            <label>
              <span>Business type</span>
              <input
                required
                value={form.businessType}
                onChange={(event) => {
                  setForm((current) => ({ ...current, businessType: event.target.value }));
                }}
              />
            </label>
          </div>
          <label>
            <span>Subscription plan</span>
            <select
              value={form.planId}
              onChange={(event) => {
                setForm((current) => ({ ...current, planId: event.target.value }));
              }}
            >
              {tenant.plan &&
                !["plan-basic", "plan-pro", "plan-master"].includes(tenant.plan.id) && (
                  <option value={tenant.plan.id}>{tenant.plan.name} (legacy)</option>
                )}
              <option value="plan-basic">Basic · Free</option>
              <option value="plan-pro">Pro</option>
              <option value="plan-master">Master</option>
            </select>
          </label>
        </fieldset>
        <fieldset className="sa-editor-section">
          <legend>Contact</legend>
          <div className="form-row">
            <label>
              <span>Business email</span>
              <input
                required
                type="email"
                value={form.businessEmail}
                onChange={(event) => {
                  setForm((current) => ({ ...current, businessEmail: event.target.value }));
                }}
              />
            </label>
            <label>
              <span>Business phone</span>
              <input
                inputMode="tel"
                value={form.businessPhone}
                onChange={(event) => {
                  setForm((current) => ({ ...current, businessPhone: event.target.value }));
                }}
              />
            </label>
          </div>
          <label>
            <span>Website</span>
            <input
              type="url"
              placeholder="https://…"
              value={form.website}
              onChange={(event) => {
                setForm((current) => ({ ...current, website: event.target.value }));
              }}
            />
          </label>
        </fieldset>
        <fieldset className="sa-editor-section">
          <legend>Regional defaults and address</legend>
          <div className="form-row form-row--three">
            <label>
              <span>Currency</span>
              <input
                required
                maxLength={3}
                value={form.defaultCurrency}
                onChange={(event) => {
                  setForm((current) => ({
                    ...current,
                    defaultCurrency: event.target.value.toUpperCase(),
                  }));
                }}
              />
            </label>
            <label>
              <span>Country</span>
              <select
                required
                value={form.country || "Nepal"}
                onChange={(event) => {
                  setForm((current) => ({ ...current, country: event.target.value }));
                }}
              >
                <option value={form.country || "Nepal"}>Nepal</option>
              </select>
            </label>
            <label>
              <span>Timezone</span>
              <input
                required
                value={form.defaultTimezone}
                onChange={(event) => {
                  setForm((current) => ({ ...current, defaultTimezone: event.target.value }));
                }}
              />
            </label>
          </div>
          <label>
            <span>Address</span>
            <input
              value={form.address}
              onChange={(event) => {
                setForm((current) => ({ ...current, address: event.target.value }));
              }}
            />
          </label>
          <NepalAddressFields
            value={{ province: form.stateProvince, district: form.district, localLevel: form.city }}
            onChange={({ province, district, localLevel }) => { setForm((current) => ({ ...current, stateProvince: province, district, city: localLevel })); }}
          />
          <div className="form-row form-row--three">
            <label>
              <span>Postal code</span>
              <input
                value={form.postalCode}
                onChange={(event) => {
                  setForm((current) => ({ ...current, postalCode: event.target.value }));
                }}
              />
            </label>
          </div>
        </fieldset>
        <fieldset className="sa-editor-section">
          <legend>Branding</legend>
          <label>
            <span>Logo URL</span>
            <input
              type="url"
              placeholder="https://…"
              value={form.logoUrl}
              onChange={(event) => {
                setForm((current) => ({ ...current, logoUrl: event.target.value }));
              }}
            />
          </label>
          <div className="form-row form-row--three sa-color-grid">
            {(
              [
                ["primaryColor", "Primary color"],
                ["secondaryColor", "Secondary color"],
                ["accentColor", "Accent color"],
              ] as const
            ).map(([key, label]) => (
              <label key={key}>
                <span>{label}</span>
                <span className="sa-color-control">
                  <input
                    aria-label={`${label} picker`}
                    type="color"
                    value={form[key]}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        [key]: event.target.value.toUpperCase(),
                      }));
                    }}
                  />
                  <input
                    aria-label={`${label} hexadecimal value`}
                    pattern="#[0-9A-Fa-f]{6}"
                    value={form[key]}
                    onChange={(event) => {
                      setForm((current) => ({ ...current, [key]: event.target.value }));
                    }}
                  />
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <section className="sa-editor-meta" aria-labelledby="business-account-metadata">
          <h3 id="business-account-metadata">Platform record</h3>
          <dl>
            <div>
              <dt>Business ID</dt>
              <dd>{tenant.publicId}</dd>
            </div>
            <div>
              <dt>PAN</dt>
              <dd>{tenant.businessPanNumber ?? "Not recorded"}</dd>
            </div>
            <div>
              <dt>Account status</dt>
              <dd>{tenant.status}</dd>
            </div>
            <div>
              <dt>Application</dt>
              <dd>{tenant.applicationStatus.replaceAll("_", " ")}</dd>
            </div>
            <div>
              <dt>Owner email</dt>
              <dd>{tenant.ownerEmailVerified ? "Verified" : "Not verified"}</dd>
            </div>
            <div>
              <dt>Setup</dt>
              <dd>{tenant.setupStatus.replaceAll("_", " ")}</dd>
            </div>
            <div>
              <dt>Locations</dt>
              <dd>{tenant.locationCount}</dd>
            </div>
            <div>
              <dt>Staff</dt>
              <dd>{tenant.staffCount}</dd>
            </div>
            <div>
              <dt>Rewards</dt>
              <dd>{tenant.rewardCount}</dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>{date(tenant.createdAt)}</dd>
            </div>
          </dl>
          <p>PAN, approval, setup, and account status follow separate controlled workflows.</p>
        </section>
        <div className="modal-actions">
          <button className="button button--quiet" type="button" onClick={close}>
            Cancel
          </button>
          <button className="button button--primary" disabled={mutation.isPending} type="submit">
            {mutation.isPending ? "Saving…" : "Save Business Owner"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const creditUnitsToMinor = (value: string) => Math.round(Number(value) * 100);

function MeroCreditAcceptanceForm({
  workspace,
  platformReady,
  close,
}: {
  workspace: MeroCreditMerchantAcceptanceWorkspace;
  platformReady: boolean;
  close: () => void;
}) {
  const queryClient = useQueryClient();
  const { acceptance, locations, tenant } = workspace;
  const [enabled, setEnabled] = useState(acceptance.enabled);
  const [minimumBasket, setMinimumBasket] = useState(
    (acceptance.minimumBasketMinor / 100).toFixed(2),
  );
  const [maximumPaymentPercent, setMaximumPaymentPercent] = useState(
    (acceptance.maximumPaymentBasisPoints / 100).toFixed(0),
  );
  const [minimumRedemption, setMinimumRedemption] = useState(
    (acceptance.minimumRedemptionMinor / 100).toFixed(2),
  );
  const [maximumRedemption, setMaximumRedemption] = useState(
    (acceptance.maximumRedemptionMinor / 100).toFixed(2),
  );
  const [locationScope, setLocationScope] = useState<"all" | "selected">(
    acceptance.eligibleLocationPublicIds.length ? "selected" : "all",
  );
  const [eligibleLocationPublicIds, setEligibleLocationPublicIds] = useState(
    acceptance.eligibleLocationPublicIds,
  );
  const [excludedCategoryCodes, setExcludedCategoryCodes] = useState(
    acceptance.excludedCategoryCodes.join(", "),
  );
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<AppAlertTone>("error");
  const mutation = useMutation({
    mutationFn: () => {
      const minimumBasketMinor = creditUnitsToMinor(minimumBasket);
      const minimumRedemptionMinor = creditUnitsToMinor(minimumRedemption);
      const maximumRedemptionMinor = creditUnitsToMinor(maximumRedemption);
      const maximumPaymentBasisPoints = Math.round(Number(maximumPaymentPercent) * 100);
      if (
        !Number.isInteger(minimumBasketMinor) ||
        !Number.isInteger(minimumRedemptionMinor) ||
        !Number.isInteger(maximumRedemptionMinor) ||
        minimumBasketMinor < 1 ||
        minimumRedemptionMinor < 1 ||
        maximumRedemptionMinor < minimumRedemptionMinor
      ) {
        throw new Error("Enter valid limits. The maximum redemption must be at least the minimum.");
      }
      if (
        !Number.isInteger(maximumPaymentBasisPoints) ||
        maximumPaymentBasisPoints < 1 ||
        maximumPaymentBasisPoints > 10_000
      ) {
        throw new Error("Maximum checkout payment must be between 1% and 100%.");
      }
      const activeLocationIds = new Set(
        locations
          .filter((location) => location.status === "active")
          .map((location) => location.publicId),
      );
      const selectedLocations = eligibleLocationPublicIds.filter((publicId) =>
        activeLocationIds.has(publicId),
      );
      if (locationScope === "selected" && selectedLocations.length === 0) {
        throw new Error("Select at least one active location, or allow all active locations.");
      }
      return updateMeroCreditMerchantAcceptance(tenant.publicId, {
        enabled,
        minimumBasketMinor,
        maximumPaymentBasisPoints,
        minimumRedemptionMinor,
        maximumRedemptionMinor,
        excludedCategoryCodes: Array.from(
          new Set(
            excludedCategoryCodes
              .split(",")
              .map((code) => code.trim().toUpperCase())
              .filter(Boolean),
          ),
        ),
        eligibleLocationPublicIds: locationScope === "all" ? [] : selectedLocations,
      });
    },
    onSuccess: async () => {
      setMessageTone("success");
      setMessage(
        enabled
          ? `${tenant.businessName} can now accept Mero Credits under these checkout limits.`
          : `Mero Credit acceptance is disabled for ${tenant.businessName}.`,
      );
      await queryClient.invalidateQueries({
        queryKey: ["mero-credit-acceptance", tenant.publicId],
      });
    },
    onError: (caught) => {
      setMessageTone("error");
      setMessage(messageFromError(caught));
    },
  });

  const toggleLocation = (publicId: string) => {
    setEligibleLocationPublicIds((current) =>
      current.includes(publicId)
        ? current.filter((candidate) => candidate !== publicId)
        : [...current, publicId],
    );
  };

  return (
    <form
      className="modal-form form-stack mc-acceptance-editor"
      onSubmit={(event) => {
        event.preventDefault();
        setMessage("");
        mutation.mutate();
      }}
    >
      {message && <AppAlert tone={messageTone} message={message} />}
      {!platformReady && (
        <AppAlert
          tone="warning"
          message="Platform Mero Credit redemption is not fully enabled. Save these business terms now, then approve regulation and enable cross-merchant redemption in Settings."
        />
      )}
      <section className="mc-acceptance-section mc-acceptance-section--switch">
        <div>
          <strong>Accept Mero Credits</strong>
          <p>Allow customers to apply their platform-wide Mero Credits at this business.</p>
        </div>
        <label className="mc-acceptance-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => {
              setEnabled(event.target.checked);
            }}
          />
          <span aria-hidden="true" />
          <b>{enabled ? "Enabled" : "Disabled"}</b>
        </label>
      </section>

      <section className="mc-acceptance-section">
        <div className="mc-acceptance-heading">
          <div>
            <p className="eyebrow">Checkout limits</p>
            <h3>Control how credits are applied</h3>
          </div>
          <Coins />
        </div>
        <div className="form-row">
          <label>
            <span>Minimum purchase amount</span>
            <input
              required
              min="0.01"
              step="0.01"
              inputMode="decimal"
              type="number"
              value={minimumBasket}
              onChange={(event) => {
                setMinimumBasket(event.target.value);
              }}
            />
          </label>
          <label>
            <span>Maximum checkout payment</span>
            <span className="mc-acceptance-input-suffix">
              <input
                required
                min="1"
                max="100"
                step="1"
                inputMode="numeric"
                type="number"
                value={maximumPaymentPercent}
                onChange={(event) => {
                  setMaximumPaymentPercent(event.target.value);
                }}
              />
              <i>%</i>
            </span>
          </label>
        </div>
        <div className="form-row">
          <label>
            <span>Minimum Mero Credits redemption</span>
            <input
              required
              min="0.01"
              step="0.01"
              inputMode="decimal"
              type="number"
              value={minimumRedemption}
              onChange={(event) => {
                setMinimumRedemption(event.target.value);
              }}
            />
          </label>
          <label>
            <span>Maximum Mero Credits redemption</span>
            <input
              required
              min="0.01"
              step="0.01"
              inputMode="decimal"
              type="number"
              value={maximumRedemption}
              onChange={(event) => {
                setMaximumRedemption(event.target.value);
              }}
            />
          </label>
        </div>
      </section>

      <fieldset className="mc-acceptance-section mc-acceptance-locations">
        <legend>Eligible locations</legend>
        <p>Choose where this business accepts Mero Credits.</p>
        <label className="mc-acceptance-choice">
          <input
            type="radio"
            name="location-scope"
            checked={locationScope === "all"}
            onChange={() => {
              setLocationScope("all");
            }}
          />
          <span>
            <strong>All active locations</strong>
            <small>New active locations are included automatically.</small>
          </span>
        </label>
        <label className="mc-acceptance-choice">
          <input
            type="radio"
            name="location-scope"
            checked={locationScope === "selected"}
            onChange={() => {
              setLocationScope("selected");
            }}
          />
          <span>
            <strong>Selected locations only</strong>
            <small>Use this for a controlled rollout.</small>
          </span>
        </label>
        {locationScope === "selected" && (
          <div className="mc-acceptance-location-list">
            {locations.length ? (
              locations.map((location) => (
                <label key={location.publicId}>
                  <input
                    type="checkbox"
                    disabled={location.status !== "active"}
                    checked={eligibleLocationPublicIds.includes(location.publicId)}
                    onChange={() => {
                      toggleLocation(location.publicId);
                    }}
                  />
                  <span>
                    <strong>{location.name}</strong>
                    <small>
                      {location.code}
                      {location.city ? ` · ${location.city}` : ""} · {location.status}
                    </small>
                  </span>
                </label>
              ))
            ) : (
              <p className="mc-acceptance-empty">This business has no locations yet.</p>
            )}
          </div>
        )}
      </fieldset>

      <label>
        <span>Excluded item category codes (optional)</span>
        <input
          value={excludedCategoryCodes}
          placeholder="ALCOHOL, TOBACCO"
          onChange={(event) => {
            setExcludedCategoryCodes(event.target.value);
          }}
        />
        <small>Separate codes with commas. Leave empty when every category is eligible.</small>
      </label>

      <div className="modal-actions">
        <button className="button button--quiet" type="button" onClick={close}>
          Close
        </button>
        <button className="button button--primary" disabled={mutation.isPending} type="submit">
          {mutation.isPending ? "Saving…" : "Save Mero Credit settings"}
        </button>
      </div>
    </form>
  );
}

export function PlatformMeroCreditAcceptanceEditor({
  tenant,
  close,
}: {
  tenant: Tenant;
  close: () => void;
}) {
  const acceptance = useQuery({
    queryKey: ["mero-credit-acceptance", tenant.publicId],
    queryFn: () => fetchMeroCreditMerchantAcceptance(tenant.publicId),
  });
  const configuration = useQuery({
    queryKey: ["mero-credit-configuration"],
    queryFn: fetchMeroCreditConfiguration,
  });
  const platformReady =
    configuration.data?.regulatoryState === "APPROVED" &&
    configuration.data.globalRedemptionEnabled;

  return (
    <Modal title={`Mero Credits · ${tenant.name}`} eyebrow="Business acceptance" close={close}>
      {acceptance.isLoading || configuration.isLoading ? (
        <Loading label="Loading Mero Credit settings" />
      ) : acceptance.isError || !acceptance.data ? (
        <div className="form-stack">
          <AppAlert message={messageFromError(acceptance.error)} />
          <div className="modal-actions">
            <button className="button button--quiet" type="button" onClick={close}>
              Close
            </button>
          </div>
        </div>
      ) : (
        <MeroCreditAcceptanceForm
          key={`${tenant.publicId}-${acceptance.data.acceptance.updatedAt ?? "default"}`}
          workspace={acceptance.data}
          platformReady={platformReady}
          close={close}
        />
      )}
    </Modal>
  );
}

export function SuperAdminUsersView() {
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ["platform-users"], queryFn: fetchPlatformUsers });
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<PlatformUser | null>(null);
  const [userToArchive, setUserToArchive] = useState<PlatformUser | null>(null);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<AppAlertTone>("error");
  const status = useMutation({
    mutationFn: ({ user, next }: { user: PlatformUser; next: PlatformUser["status"] }) =>
      setPlatformUserStatus(user.publicId, next),
    onSuccess: async () => {
      setMessage("");
      await queryClient.invalidateQueries({ queryKey: ["platform-users"] });
    },
    onError: (error) => {
      setMessageTone("error");
      setMessage(messageFromError(error));
    },
  });
  const reset = useMutation({
    mutationFn: sendPlatformUserPasswordReset,
    onSuccess: (result) => {
      setMessageTone(result.delivery === "sent" ? "success" : "warning");
      setMessage(
        result.delivery === "sent"
          ? `Password reset sent to ${result.email}.`
          : `The reset was created, but email delivery is temporarily unavailable for ${result.email}.`,
      );
    },
    onError: (error) => {
      setMessageTone("error");
      setMessage(messageFromError(error));
    },
  });
  const archive = useMutation({
    mutationFn: ({ user, confirmation }: { user: PlatformUser; confirmation: string }) =>
      archivePlatformUser(user.publicId, confirmation),
    onSuccess: async (_archive, variables) => {
      setUserToArchive(null);
      setMessageTone("success");
      setMessage(
        `${variables.user.email} and its related account records were archived. The email address is available for future registration.`,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["platform-users"] }),
        queryClient.invalidateQueries({ queryKey: ["platform-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["tenants"] }),
        queryClient.invalidateQueries({ queryKey: ["public-rewards"] }),
        queryClient.invalidateQueries({ queryKey: ["marketplace"] }),
        queryClient.invalidateQueries({ queryKey: ["marketplace-types"] }),
        queryClient.invalidateQueries({ queryKey: ["customer-experience"] }),
      ]);
    },
  });
  const filtered = useMemo(
    () =>
      (users.data ?? []).filter((user) =>
        `${user.firstName} ${user.lastName} ${user.email} ${user.roles.join(" ")} ${user.tenantNames.join(" ")}`
          .toLowerCase()
          .includes(search.trim().toLowerCase()),
      ),
    [search, users.data],
  );
  return (
    <main className="portal-content sa-page">
      <section className="welcome-row">
        <div>
          <p className="eyebrow">Platform access</p>
          <h1>Users</h1>
          <p>Manage every customer, Business Owner staff member, and Super Admin identity.</p>
        </div>
        <span className="sa-title-icon">
          <Users />
        </span>
      </section>
      <ActionMessage
        message={message}
        tone={messageTone}
        clear={() => {
          setMessage("");
        }}
      />
      <section className="portal-panel">
        <div className="panel-heading sa-list-heading">
          <div>
            <h2>All users</h2>
            <p>
              {number(filtered.length)} of {number(users.data?.length ?? 0)} accounts shown
            </p>
          </div>
          <label className="sa-search">
            <Search />
            <span className="sr-only">Search users</span>
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              placeholder="Search name, email, role, or Business Owner"
            />
          </label>
        </div>
        {users.isLoading ? (
          <Loading label="Loading users" />
        ) : users.isError ? (
          <AppAlert message={messageFromError(users.error)} />
        ) : (
          <div className="data-table-wrap">
            <table className="data-table sa-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Access</th>
                  <th>Business Owners</th>
                  <th>Verification</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user) => (
                  <tr key={user.publicId}>
                    <td>
                      <div className="table-primary">
                        <span className="table-monogram">
                          {(user.firstName[0] ?? user.email[0] ?? "U").toUpperCase()}
                          {(user.lastName[0] ?? "").toUpperCase()}
                        </span>
                        <div>
                          <strong>
                            {`${user.firstName} ${user.lastName}`.trim() || "Unnamed user"}
                          </strong>
                          <small>
                            {user.email}
                            {user.phone ? ` · ${user.phone}` : ""}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td data-label="Access">
                      <div className="sa-chip-row">
                        {user.roles.map((role) => (
                          <span className="sa-chip" key={role}>
                            {role.replace("_", " ")}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td data-label="Business Owners">{user.tenantNames.join(", ") || "—"}</td>
                    <td data-label="Verification">
                      <span
                        className={`status-pill status-pill--${user.emailVerified ? "active" : "draft"}`}
                      >
                        {user.emailVerified ? "Verified" : "Pending"}
                      </span>
                    </td>
                    <td data-label="Status">
                      <span className={`status-pill status-pill--${user.status}`}>
                        {user.status}
                      </span>
                    </td>
                    <td>
                      <div className="sa-row-actions">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(user);
                          }}
                        >
                          <Edit3 /> Edit
                        </button>
                        <button
                          type="button"
                          disabled={reset.isPending}
                          onClick={() => {
                            reset.mutate(user.publicId);
                          }}
                        >
                          <KeyRound /> Reset
                        </button>
                        <button
                          className={user.status === "active" ? "is-danger" : ""}
                          type="button"
                          disabled={status.isPending}
                          onClick={() => {
                            status.mutate({
                              user,
                              next: user.status === "active" ? "suspended" : "active",
                            });
                          }}
                        >
                          {user.status === "active" ? "Suspend" : "Activate"}
                        </button>
                        <button
                          className="is-danger"
                          type="button"
                          disabled={archive.isPending || user.roles.includes("super_admin")}
                          title={
                            user.roles.includes("super_admin")
                              ? "Super Admin accounts are protected from deletion."
                              : "Delete and archive this user account"
                          }
                          onClick={() => {
                            archive.reset();
                            setUserToArchive(user);
                          }}
                        >
                          <Trash2 /> {user.roles.includes("super_admin") ? "Protected" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filtered.length && (
              <div className="empty-state">
                <Users />
                <h3>No matching users</h3>
                <p>Try a different name, email, role, or Business Owner.</p>
              </div>
            )}
          </div>
        )}
      </section>
      {editing && (
        <UserEditor
          user={editing}
          close={() => {
            setEditing(null);
          }}
        />
      )}
      {userToArchive && (
        <ArchiveAccountDialog
          entityKind="user"
          entityLabel={userToArchive.email}
          confirmation={userToArchive.email}
          busy={archive.isPending}
          error={archive.isError ? messageFromError(archive.error) : undefined}
          impact={[
            "Every active session will be revoked and this profile, its memberships, balances, rewards, and account activity will leave the live platform.",
            "The email address will be released for a new account.",
            "Business transaction records remain preserved in the archive for audit. A protected or sole-owner account may need another owner before it can be deleted.",
          ]}
          onCancel={() => {
            if (archive.isPending) return;
            archive.reset();
            setUserToArchive(null);
          }}
          onConfirm={(confirmation) => {
            archive.mutate({ user: userToArchive, confirmation });
          }}
        />
      )}
    </main>
  );
}

function TierRuleEditor({ rule, close }: { rule: TierAdminRule | null; close: () => void }) {
  const queryClient = useQueryClient();
  const [spend, setSpend] = useState(String((rule?.spendUnitMinor ?? 10000) / 100));
  const [points, setPoints] = useState(String(rule?.pointsPerUnit ?? 1));
  const [minimum, setMinimum] = useState(String((rule?.minimumQualifyingMinor ?? 10000) / 100));
  const [maximum, setMaximum] = useState(
    rule?.maximumPointsPerTransaction ? String(rule.maximumPointsPerTransaction) : "",
  );
  const [effective, setEffective] = useState(dateTimeLocal(rule?.effectiveFrom));
  const [error, setError] = useState("");
  const mutation = useMutation({
    mutationFn: (input: TierRuleInput) =>
      rule ? updateTierRule(rule.publicId, input) : createTierRule(input),
  });
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    try {
      await mutation.mutateAsync({
        spendUnitMinor: Math.round(Number(spend) * 100),
        tierPointsPerUnit: Number(points),
        minimumQualifyingMinor: Math.round(Number(minimum) * 100),
        maximumTierPointsPerTransaction: maximum ? Number(maximum) : null,
        effectiveFrom: new Date(effective).toISOString(),
      });
      await queryClient.invalidateQueries({ queryKey: ["tier-admin-program"] });
      close();
    } catch (caught) {
      setError(messageFromError(caught));
    }
  };
  return (
    <Modal
      title={
        rule ? `Edit Tier Point rule v${String(rule.versionNumber)}` : "Create Tier Point rule"
      }
      eyebrow="Effective-dated policy"
      close={close}
    >
      <form className="modal-form form-stack" onSubmit={(event) => void submit(event)}>
        {error && <AppAlert message={error} />}
        <div className="form-row">
          <label>
            <span>Spend unit (NPR)</span>
            <input
              required
              min="0.01"
              step="0.01"
              type="number"
              value={spend}
              onChange={(event) => {
                setSpend(event.target.value);
              }}
            />
          </label>
          <label>
            <span>Tier Points per unit</span>
            <input
              required
              min="1"
              type="number"
              value={points}
              onChange={(event) => {
                setPoints(event.target.value);
              }}
            />
          </label>
        </div>
        <div className="form-row">
          <label>
            <span>Minimum qualifying spend</span>
            <input
              required
              min="0"
              step="0.01"
              type="number"
              value={minimum}
              onChange={(event) => {
                setMinimum(event.target.value);
              }}
            />
          </label>
          <label>
            <span>Maximum points per purchase</span>
            <input
              min="1"
              type="number"
              value={maximum}
              onChange={(event) => {
                setMaximum(event.target.value);
              }}
              placeholder="No maximum"
            />
          </label>
        </div>
        <label>
          <span>Effective from</span>
          <input
            required
            type="datetime-local"
            value={effective}
            onChange={(event) => {
              setEffective(event.target.value);
            }}
          />
        </label>
        <div className="modal-actions">
          <button className="button button--quiet" type="button" onClick={close}>
            Cancel
          </button>
          <button className="button button--primary" disabled={mutation.isPending} type="submit">
            {mutation.isPending ? "Saving…" : rule ? "Save draft" : "Create draft"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function creditInput(rule?: MeroCreditRule | null): MeroCreditRuleInput {
  return {
    code: rule?.code ?? "",
    name: rule?.name ?? "",
    description: rule?.description ?? "",
    audience: rule ? (rule.audience as MeroCreditRuleInput["audience"]) : "CUSTOMER",
    triggerType: rule
      ? (rule.triggerType as MeroCreditRuleInput["triggerType"])
      : "FIRST_QUALIFIED_PURCHASE",
    awardMinor: rule?.awardMinor ?? 500,
    fundingBucket: rule
      ? (rule.fundingBucket as MeroCreditRuleInput["fundingBucket"])
      : "PROMOTIONAL",
    eligibility: rule?.eligibility ?? {},
    frequencyType: rule
      ? (rule.frequencyType as MeroCreditRuleInput["frequencyType"])
      : "ONCE_LIFETIME",
    maximumAwardsPerHolder: rule?.maximumAwardsPerHolder ?? 1,
    totalBudgetMinor: rule?.totalBudgetMinor ?? null,
    expiryMode: rule?.expiryMode ?? "DAYS",
    expiryDays: rule?.expiryDays ?? 90,
    expiresAt: rule?.expiresAt ?? null,
    stackingMode: rule?.stackingMode ?? "STACKABLE",
    priority: rule?.priority ?? 100,
    effectiveFrom: rule?.effectiveFrom ?? new Date().toISOString(),
    effectiveTo: rule?.effectiveTo ?? null,
    changeReason: rule?.changeReason || "Managed in Super Admin Earning Rules",
  };
}

function meroCreditAppliedLabel(rule: MeroCreditRule): string {
  if (rule.status === "ACTIVE" && rule.activeVersionNumber) {
    if (new Date(rule.effectiveFrom).getTime() > Date.now()) {
      return `Scheduled: version ${number(rule.activeVersionNumber)} begins ${dateTime(rule.effectiveFrom)}`;
    }
    return `Currently applied: version ${number(rule.activeVersionNumber)}${rule.versionStatus === "DRAFT" ? ` (draft version ${number(rule.versionNumber)} is not applied yet)` : ""}`;
  }
  if (rule.status === "PAUSED" && rule.activeVersionNumber) {
    return `Not currently active: version ${number(rule.activeVersionNumber)} is paused and can be activated again`;
  }
  if (rule.activeVersionNumber) {
    return `Not currently active: version ${number(rule.activeVersionNumber)} is retained while the rule is ${rule.status.toLowerCase()}`;
  }
  return "Currently applied: none — activate the draft to begin awarding credits";
}

function MeroCreditRuleEditor({
  rule,
  versionOnly,
  close,
}: {
  rule: MeroCreditRule | null;
  versionOnly?: boolean | undefined;
  close: () => void;
}) {
  const queryClient = useQueryClient();
  const initial = creditInput(rule);
  const [form, setForm] = useState({
    ...initial,
    award: String(initial.awardMinor / 100),
    budget: initial.totalBudgetMinor ? String(initial.totalBudgetMinor / 100) : "",
    expiry: String(initial.expiryDays ?? 90),
    effective: dateTimeLocal(initial.effectiveFrom),
  });
  const [error, setError] = useState("");
  const mutation = useMutation({
    mutationFn: async (input: MeroCreditRuleInput) => {
      if (!rule) return createPlatformMeroCreditRule(input);
      if (versionOnly) {
        const version = {
          awardMinor: input.awardMinor,
          fundingBucket: input.fundingBucket,
          eligibility: input.eligibility,
          frequencyType: input.frequencyType,
          maximumAwardsPerHolder: input.maximumAwardsPerHolder,
          totalBudgetMinor: input.totalBudgetMinor,
          expiryMode: input.expiryMode,
          expiryDays: input.expiryDays,
          expiresAt: input.expiresAt,
          stackingMode: input.stackingMode,
          priority: input.priority,
          effectiveFrom: input.effectiveFrom,
          effectiveTo: input.effectiveTo,
          changeReason: input.changeReason,
        };
        return createMeroCreditRuleDraft(rule.publicId, version);
      }
      return updateMeroCreditRule(rule.publicId, input);
    },
  });
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    const input: MeroCreditRuleInput = {
      code: form.code
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9_]/g, "_"),
      name: form.name.trim(),
      description: form.description.trim(),
      audience: form.audience,
      triggerType: form.triggerType,
      awardMinor: Math.round(Number(form.award) * 100),
      fundingBucket: form.fundingBucket,
      eligibility: form.eligibility,
      frequencyType: form.frequencyType,
      maximumAwardsPerHolder: form.maximumAwardsPerHolder,
      totalBudgetMinor: form.budget ? Math.round(Number(form.budget) * 100) : null,
      expiryMode: form.expiryMode,
      expiryDays: form.expiryMode === "DAYS" ? Number(form.expiry) : null,
      expiresAt: form.expiresAt,
      stackingMode: form.stackingMode,
      priority: form.priority,
      effectiveFrom: new Date(form.effective).toISOString(),
      effectiveTo: form.effectiveTo,
      changeReason: form.changeReason.trim(),
    };
    try {
      await mutation.mutateAsync(input);
      await queryClient.invalidateQueries({ queryKey: ["mero-credits", "admin", "rules"] });
      close();
    } catch (caught) {
      setError(messageFromError(caught));
    }
  };
  const metadataLocked = Boolean(versionOnly);
  return (
    <Modal
      title={
        !rule
          ? "Create Mero Credit rule"
          : versionOnly
            ? `Create draft for ${rule.name}`
            : `Edit ${rule.name}`
      }
      eyebrow="Mero Credits earning policy"
      close={close}
    >
      <form className="modal-form form-stack" onSubmit={(event) => void submit(event)}>
        {error && <AppAlert message={error} />}
        <div className="form-row">
          <label>
            <span>Rule code</span>
            <input
              required
              minLength={2}
              disabled={metadataLocked}
              value={form.code}
              onChange={(event) => {
                setForm((current) => ({ ...current, code: event.target.value }));
              }}
            />
          </label>
          <label>
            <span>Rule name</span>
            <input
              required
              minLength={3}
              disabled={metadataLocked}
              value={form.name}
              onChange={(event) => {
                setForm((current) => ({ ...current, name: event.target.value }));
              }}
            />
          </label>
        </div>
        <label>
          <span>Description</span>
          <textarea
            required
            minLength={10}
            disabled={metadataLocked}
            rows={3}
            value={form.description}
            onChange={(event) => {
              setForm((current) => ({ ...current, description: event.target.value }));
            }}
          />
        </label>
        <div className="form-row">
          <label>
            <span>Audience</span>
            <select
              disabled={metadataLocked}
              value={form.audience}
              onChange={(event) => {
                setForm((current) => ({
                  ...current,
                  audience: event.target.value as MeroCreditRuleInput["audience"],
                }));
              }}
            >
              <option value="CUSTOMER">Customers</option>
              <option value="BUSINESS">Businesses</option>
              <option value="BOTH">Both</option>
            </select>
          </label>
          <label>
            <span>Trigger</span>
            <select
              disabled={metadataLocked}
              value={form.triggerType}
              onChange={(event) => {
                setForm((current) => ({
                  ...current,
                  triggerType: event.target.value as MeroCreditRuleInput["triggerType"],
                }));
              }}
            >
              <option value="VERIFIED_FIRST_SIGNUP">Verified first signup</option>
              <option value="TENANT_APPROVED">Business Owner approved</option>
              <option value="FIRST_QUALIFIED_PURCHASE">First qualified purchase</option>
              <option value="TIER_PROMOTED">Tier promoted</option>
              <option value="REFERRAL_QUALIFIED">Referral qualified</option>
              <option value="BIRTHDAY">Birthday</option>
              <option value="MEMBERSHIP_ANNIVERSARY">Membership anniversary</option>
              <option value="TRANSACTION_MILESTONE">Transaction milestone</option>
              <option value="CROSS_BUSINESS_CHALLENGE">Cross-business challenge</option>
              <option value="CAMPAIGN">Campaign</option>
              <option value="ADMIN_EVENT">Admin event</option>
            </select>
          </label>
        </div>
        <div className="form-row">
          <label>
            <span>Award (Mero Credits)</span>
            <input
              required
              min="0.01"
              step="0.01"
              type="number"
              value={form.award}
              onChange={(event) => {
                setForm((current) => ({ ...current, award: event.target.value }));
              }}
            />
          </label>
          <label>
            <span>Total budget (Mero Credits)</span>
            <input
              min="0.01"
              step="0.01"
              type="number"
              value={form.budget}
              onChange={(event) => {
                setForm((current) => ({ ...current, budget: event.target.value }));
              }}
              placeholder="No limit"
            />
          </label>
        </div>
        <div className="form-row">
          <label>
            <span>Expiry days</span>
            <input
              required
              min="1"
              type="number"
              value={form.expiry}
              onChange={(event) => {
                setForm((current) => ({ ...current, expiry: event.target.value }));
              }}
            />
          </label>
          <label>
            <span>Effective from</span>
            <input
              required
              type="datetime-local"
              value={form.effective}
              onChange={(event) => {
                setForm((current) => ({ ...current, effective: event.target.value }));
              }}
            />
          </label>
        </div>
        <label>
          <span>Change reason</span>
          <input
            required
            minLength={5}
            value={form.changeReason}
            onChange={(event) => {
              setForm((current) => ({ ...current, changeReason: event.target.value }));
            }}
          />
        </label>
        <div className="modal-actions">
          <button className="button button--quiet" type="button" onClick={close}>
            Cancel
          </button>
          <button className="button button--primary" disabled={mutation.isPending} type="submit">
            {mutation.isPending
              ? "Saving…"
              : versionOnly
                ? "Create editable draft"
                : rule
                  ? "Save rule"
                  : "Create draft rule"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function SuperAdminEarningRulesView() {
  const queryClient = useQueryClient();
  const tier = useQuery({ queryKey: ["tier-admin-program"], queryFn: fetchTierAdminProgram });
  const credits = useQuery({
    queryKey: ["mero-credits", "admin", "rules"],
    queryFn: fetchMeroCreditRules,
  });
  const [tierEditor, setTierEditor] = useState<TierAdminRule | "new" | null>(null);
  const [creditEditor, setCreditEditor] = useState<{
    rule: MeroCreditRule | null;
    versionOnly?: boolean;
  } | null>(null);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<AppAlertTone>("error");
  const tierRules = tier.data?.rules ?? [];
  const activeTierRule = tierRules.find((rule) => rule.status === "active") ?? null;
  const currentTierRule =
    tierRules.find((rule) => rule.status === "draft") ?? activeTierRule ?? tierRules[0] ?? null;
  const refreshTier = async () => {
    await queryClient.invalidateQueries({ queryKey: ["tier-admin-program"] });
  };
  const refreshCredits = async () => {
    await queryClient.invalidateQueries({ queryKey: ["mero-credits", "admin", "rules"] });
  };
  const tierAction = useMutation({
    mutationFn: async ({
      rule,
      action,
    }: {
      rule: TierAdminRule;
      action: "publish" | "draft" | "archive" | "delete";
    }) => {
      if (action === "publish") return publishTierRule(rule.publicId);
      if (action === "draft") return createTierRuleDraft(rule.publicId);
      if (action === "archive") return archiveTierRule(rule.publicId);
      await deleteTierRule(rule.publicId);
      return null;
    },
    onSuccess: async (result, variables) => {
      setMessage("");
      await refreshTier();
      if (variables.action === "draft" && result) setTierEditor(result as TierAdminRule);
    },
    onError: (error) => {
      setMessageTone("error");
      setMessage(messageFromError(error));
    },
  });
  const creditAction = useMutation({
    mutationFn: async ({
      rule,
      action,
    }: {
      rule: MeroCreditRule;
      action: "activate" | "pause" | "archive" | "delete";
    }) => {
      if (action === "activate") return activateMeroCreditRule(rule.publicId);
      if (action === "pause") return pauseMeroCreditRule(rule.publicId);
      if (action === "archive") return archiveMeroCreditRule(rule.publicId);
      await deleteMeroCreditRule(rule.publicId);
      return null;
    },
    onSuccess: async () => {
      setMessage("");
      await refreshCredits();
    },
    onError: (error) => {
      setMessageTone("error");
      setMessage(messageFromError(error));
    },
  });
  const assessment = useMutation({
    mutationFn: runTierAssessment,
    onSuccess: () => {
      setMessageTone("success");
      setMessage("Tier assessment completed successfully.");
    },
    onError: (error) => {
      setMessageTone("error");
      setMessage(messageFromError(error));
    },
  });
  return (
    <main className="portal-content sa-page">
      <section className="welcome-row">
        <div>
          <p className="eyebrow">Platform earning engine</p>
          <h1>Earning Rules</h1>
          <p>
            Manage effective-dated Tier Point and Mero Credit policies without mixing Business Owner
            Reward Points.
          </p>
        </div>
        <button
          className="button button--outline"
          type="button"
          disabled={assessment.isPending}
          onClick={() => {
            assessment.mutate();
          }}
        >
          <RefreshCw /> {assessment.isPending ? "Assessing…" : "Run tier assessment"}
        </button>
      </section>
      <ActionMessage
        message={message}
        tone={messageTone}
        clear={() => {
          setMessage("");
        }}
      />
      <section className="portal-panel sa-rules-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">One platform rule</p>
            <h2>Tier Point rule</h2>
            <p>
              Edit the single rule to prepare a replacement version. Activating the draft preserves
              prior versions for ledger history.
            </p>
          </div>
          {tier.data && tierRules.length === 0 && (
            <button
              className="button button--primary button--compact"
              type="button"
              onClick={() => {
                setTierEditor("new");
              }}
            >
              <Plus /> Add Tier Point rule
            </button>
          )}
        </div>
        {tier.isLoading ? (
          <Loading label="Loading Tier Point rules" />
        ) : tier.isError ? (
          <AppAlert message={messageFromError(tier.error)} />
        ) : (
          <div className="sa-rule-list">
            {(currentTierRule ? [currentTierRule] : []).map((rule) => (
              <article key={rule.publicId}>
                <div className="sa-rule-main">
                  <span className={`status-pill status-pill--${rule.status}`}>
                    {rule.status === "retired" ? "Archived" : rule.status}
                  </span>
                  <div>
                    <strong>
                      {money(rule.spendUnitMinor)} = {number(rule.pointsPerUnit)} Tier Point
                      {rule.pointsPerUnit === 1 ? "" : "s"}
                    </strong>
                    <small>
                      Version {number(rule.versionNumber)} · Effective {date(rule.effectiveFrom)}
                      {rule.effectiveTo ? ` to ${date(rule.effectiveTo)}` : ""}
                    </small>
                    {rule.status === "draft" && activeTierRule && (
                      <small className="sa-rule-applied-state">
                        Currently applied: version {number(activeTierRule.versionNumber)}. Draft
                        version {number(rule.versionNumber)} is awaiting activation.
                      </small>
                    )}
                  </div>
                </div>
                <div className="sa-rule-actions">
                  {rule.status === "active" ? (
                    <button
                      type="button"
                      disabled={tierAction.isPending}
                      onClick={() => {
                        tierAction.mutate({ rule, action: "draft" });
                      }}
                    >
                      <Edit3 /> Edit
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          if (rule.status === "draft") setTierEditor(rule);
                          else tierAction.mutate({ rule, action: "draft" });
                        }}
                      >
                        <Edit3 /> {rule.status === "draft" ? "Edit" : "Edit as draft"}
                      </button>
                      {rule.status === "draft" && (
                        <button
                          type="button"
                          disabled={tierAction.isPending}
                          onClick={() => {
                            tierAction.mutate({ rule, action: "publish" });
                          }}
                        >
                          <BadgeCheck /> Activate
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={tierAction.isPending || rule.status === "retired"}
                        onClick={() => {
                          tierAction.mutate({ rule, action: "archive" });
                        }}
                      >
                        <Archive /> {rule.status === "retired" ? "Archived" : "Archive"}
                      </button>
                      <button
                        className="is-danger"
                        type="button"
                        disabled={tierAction.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              "Delete this Tier Point rule? Rules with ledger history cannot be deleted.",
                            )
                          )
                            tierAction.mutate({ rule, action: "delete" });
                        }}
                      >
                        <Trash2 /> Delete
                      </button>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
      <section className="portal-panel sa-rules-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Rules by trigger type</p>
            <h2>Mero Credit rules</h2>
            <p>
              Multiple rules may be active when they serve different events. Each logical rule keeps
              only one active version.
            </p>
          </div>
          <button
            className="button button--primary button--compact"
            type="button"
            onClick={() => {
              setCreditEditor({ rule: null });
            }}
          >
            <Plus /> New Mero Credit rule
          </button>
        </div>
        {credits.data && (
          <div className="sa-rule-status-summary" aria-label="Mero Credit rule status summary">
            <span>
              <BadgeCheck />
              <strong>{credits.data.filter((rule) => rule.status === "ACTIVE").length}</strong>{" "}
              active
            </span>
            <span>
              <Edit3 />
              <strong>
                {credits.data.filter((rule) => rule.versionStatus === "DRAFT").length}
              </strong>{" "}
              draft
            </span>
            <span>
              <Archive />
              <strong>
                {credits.data.filter((rule) => ["PAUSED", "RETIRED"].includes(rule.status)).length}
              </strong>{" "}
              inactive
            </span>
          </div>
        )}
        {credits.isLoading ? (
          <Loading label="Loading Mero Credit rules" />
        ) : credits.isError ? (
          <AppAlert message={messageFromError(credits.error)} />
        ) : (
          <div className="sa-rule-list">
            {(credits.data ?? []).map((rule) => (
              <article key={rule.publicId}>
                <div className="sa-rule-main">
                  <div className="sa-rule-status-stack">
                    <span className={`status-pill status-pill--${rule.status.toLowerCase()}`}>
                      Rule: {rule.status === "RETIRED" ? "ARCHIVED" : rule.status}
                    </span>
                    <span
                      className={`status-pill status-pill--${rule.versionStatus.toLowerCase()}`}
                    >
                      Latest v{number(rule.versionNumber)}: {rule.versionStatus}
                    </span>
                  </div>
                  <div>
                    <strong>
                      {rule.name} · {formatCredits(rule.awardMinor)} Mero Credits
                    </strong>
                    <p>{rule.description}</p>
                    <small>
                      {rule.code} · {rule.triggerType.replaceAll("_", " ")} · v
                      {number(rule.versionNumber)} · {rule.versionStatus}
                    </small>
                    <small className="sa-rule-applied-state">{meroCreditAppliedLabel(rule)}</small>
                  </div>
                </div>
                <div className="sa-rule-actions">
                  {rule.status === "ACTIVE" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setCreditEditor({ rule, versionOnly: true });
                        }}
                      >
                        <Edit3 /> {rule.versionStatus === "DRAFT" ? "Edit draft" : "Create draft"}
                      </button>
                      {rule.versionStatus === "DRAFT" && (
                        <button
                          type="button"
                          disabled={creditAction.isPending}
                          onClick={() => {
                            creditAction.mutate({ rule, action: "activate" });
                          }}
                        >
                          <BadgeCheck /> Apply draft
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={creditAction.isPending}
                        onClick={() => {
                          creditAction.mutate({ rule, action: "pause" });
                        }}
                      >
                        Pause
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setCreditEditor({ rule });
                        }}
                      >
                        <Edit3 /> Edit
                      </button>
                      {(rule.versionStatus === "DRAFT" ||
                        (rule.status === "PAUSED" && Boolean(rule.activeVersionNumber))) && (
                        <button
                          type="button"
                          disabled={creditAction.isPending}
                          onClick={() => {
                            creditAction.mutate({ rule, action: "activate" });
                          }}
                        >
                          <BadgeCheck />
                          {rule.versionStatus === "DRAFT" ? "Activate draft" : "Activate"}
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={creditAction.isPending || rule.status === "RETIRED"}
                        onClick={() => {
                          creditAction.mutate({ rule, action: "archive" });
                        }}
                      >
                        <Archive /> {rule.status === "RETIRED" ? "Archived" : "Archive"}
                      </button>
                      <button
                        className="is-danger"
                        type="button"
                        disabled={creditAction.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              "Delete this Mero Credit rule? Rules with ledger history cannot be deleted.",
                            )
                          )
                            creditAction.mutate({ rule, action: "delete" });
                        }}
                      >
                        <Trash2 /> Delete
                      </button>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
      {tierEditor && (
        <TierRuleEditor
          rule={tierEditor === "new" ? null : tierEditor}
          close={() => {
            setTierEditor(null);
          }}
        />
      )}
      {creditEditor && (
        <MeroCreditRuleEditor
          rule={creditEditor.rule}
          versionOnly={creditEditor.versionOnly}
          close={() => {
            setCreditEditor(null);
          }}
        />
      )}
    </main>
  );
}

function PlatformSettingsForm({ settings }: { settings: PlatformSettings }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(settings);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<AppAlertTone>("error");
  const mutation = useMutation({
    mutationFn: updatePlatformSettings,
    onSuccess: async () => {
      setMessageTone("success");
      setMessage("Platform settings saved.");
      await queryClient.invalidateQueries({ queryKey: ["platform-settings"] });
    },
    onError: (error) => {
      setMessageTone("error");
      setMessage(messageFromError(error));
    },
  });
  return (
    <form
      className="sa-settings-form"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate(form);
      }}
    >
      <ActionMessage
        message={message}
        tone={messageTone}
        clear={() => {
          setMessage("");
        }}
      />
      <div className="form-row">
        <label>
          <span>Platform name</span>
          <input
            required
            value={form.platformName}
            onChange={(event) => {
              setForm((current) => ({ ...current, platformName: event.target.value }));
            }}
          />
        </label>
        <label>
          <span>Support email</span>
          <input
            required
            type="email"
            value={form.supportEmail}
            onChange={(event) => {
              setForm((current) => ({ ...current, supportEmail: event.target.value }));
            }}
          />
        </label>
      </div>
      <div className="form-row">
        <label>
          <span>Default timezone</span>
          <input
            required
            value={form.defaultTimezone}
            onChange={(event) => {
              setForm((current) => ({ ...current, defaultTimezone: event.target.value }));
            }}
          />
        </label>
        <label>
          <span>Default country</span>
          <input
            required
            value={form.defaultCountry}
            onChange={(event) => {
              setForm((current) => ({ ...current, defaultCountry: event.target.value }));
            }}
          />
        </label>
      </div>
      <div className="form-row">
        <label>
          <span>Privacy policy URL</span>
          <input
            type="url"
            value={form.privacyPolicyUrl ?? ""}
            onChange={(event) => {
              setForm((current) => ({ ...current, privacyPolicyUrl: event.target.value || null }));
            }}
          />
        </label>
        <label>
          <span>Terms URL</span>
          <input
            type="url"
            value={form.termsUrl ?? ""}
            onChange={(event) => {
              setForm((current) => ({ ...current, termsUrl: event.target.value || null }));
            }}
          />
        </label>
      </div>
      <button className="button button--primary" type="submit" disabled={mutation.isPending}>
        <ShieldCheck /> {mutation.isPending ? "Saving…" : "Save platform settings"}
      </button>
    </form>
  );
}

export function SuperAdminSettingsView() {
  const platform = useQuery({ queryKey: ["platform-settings"], queryFn: fetchPlatformSettings });
  if (platform.isLoading) return <Loading label="Opening platform settings" />;
  if (platform.isError || !platform.data)
    return <AppAlert message={messageFromError(platform.error)} />;
  return (
    <main className="portal-content sa-page">
      <section className="welcome-row">
        <div>
          <p className="eyebrow">Central configuration</p>
          <h1>Settings</h1>
          <p>Manage platform identity, support details, localization, and legal links.</p>
        </div>
        <span className="sa-title-icon">
          <Settings2 />
        </span>
      </section>
      <section className="portal-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Platform defaults</p>
            <h2>Mero Perks configuration</h2>
            <p>
              Shared settings used by platform-level services and future Business Owner defaults.
            </p>
          </div>
          <UserRoundCog />
        </div>
        <PlatformSettingsForm settings={platform.data} />
      </section>
    </main>
  );
}
